import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { DateQuestion, DateWithEmbryoAge } from '../profile-question.model';

type Column = 'month' | 'day' | 'year';

/** Pixels of vertical drag that move a column by one row. */
const DRAG_STEP = 40;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Month / day / year wheels, styled like the vertical picker and the app's date screens.
 * Only dates inside the question's window can be picked; with `embryoAge` it adds the app's
 * "Age of embryo" toggle.
 */
@Component({
  selector: 'app-date-question',
  standalone: true,
  imports: [NgClass, NgFor, NgIf],
  template: `
    <div class="flex flex-col items-center gap-[53px] md:gap-[38px]">
      <h2 class="max-w-[275px] text-center text-2xl font-bold leading-[30px] md:max-w-none">{{ question.title }}</h2>

      <!-- Rows sit at 40 / 97 / 154px; the selected row is boxed -->
      <div class="relative h-[194px] w-[300px] max-w-full select-none">
        <div class="pointer-events-none absolute inset-x-0 top-[71px] h-[56px] rounded-[5px] border-2 border-[#5D38F1] bg-white shadow-[inset_0px_4px_10px_0px_rgba(0,0,0,0.25)]"></div>

        <div class="relative grid h-full grid-cols-[1fr_1px_1fr_1px_1fr]">
          <ng-container *ngFor="let column of columns; let last = last">
            <div
              class="relative h-full touch-none outline-none"
              tabindex="0"
              role="spinbutton"
              [attr.aria-label]="column"
              [attr.aria-valuetext]="label(column, 0)"
              (keydown.arrowup)="step(column, -1); $event.preventDefault()"
              (keydown.arrowdown)="step(column, 1); $event.preventDefault()"
              (wheel)="onWheel(column, $event)"
              (pointerdown)="onPointerDown(column, $event)"
              (pointermove)="onPointerMove($event)"
              (pointerup)="onPointerUp()"
              (pointercancel)="onPointerUp()"
            >
              <button *ngIf="label(column, -1) !== null" type="button" tabindex="-1" class="absolute inset-x-0 top-[40px] -translate-y-1/2 text-center text-[26px] font-medium leading-none text-[#BEBDC1]" (click)="pick(column, -1)">{{ label(column, -1) }}</button>
              <span class="absolute inset-x-0 top-[97px] -translate-y-1/2 text-center text-4xl font-black leading-none">{{ label(column, 0) }}</span>
              <button *ngIf="label(column, 1) !== null" type="button" tabindex="-1" class="absolute inset-x-0 top-[154px] -translate-y-1/2 text-center text-[26px] font-medium leading-none text-[#BEBDC1]" (click)="pick(column, 1)">{{ label(column, 1) }}</button>
            </div>
            <div *ngIf="!last" class="my-[21px] w-px bg-black/10"></div>
          </ng-container>
        </div>
      </div>

      <!-- Age of embryo: the white pill marks the selected option -->
      <div *ngIf="question.embryoAge" class="flex w-full flex-col items-center gap-[18px] border-t border-[#DAD6FB] pt-[38px]">
        <h3 class="text-2xl font-bold leading-[30px]">Age of embryo</h3>
        <div class="flex w-full max-w-[355px] rounded-full bg-[#0F0F0F] p-[2px]" role="radiogroup" aria-label="Age of embryo">
          <button
            *ngFor="let days of embryoAges"
            type="button"
            role="radio"
            [attr.aria-checked]="embryoAgeDays === days"
            class="h-[46px] flex-1 rounded-full text-base"
            [ngClass]="embryoAgeDays === days ? 'border-2 border-black bg-white text-black' : 'text-white'"
            (click)="embryoAgeDays = days"
          >{{ days }} days</button>
        </div>
      </div>

      <button
        type="button"
        class="flex h-[55px] w-full items-center justify-center rounded-full bg-black px-9 text-[15px] font-bold uppercase leading-5 tracking-[1.5px] text-white"
        (click)="submit()"
      >Next</button>
    </div>
  `
})
export class DateQuestionComponent implements OnInit {
  @Input({ required: true }) question!: DateQuestion;
  /** `YYYY-MM-DD`, or the date with the embryo's age for `embryoAge` questions. */
  @Output() answer = new EventEmitter<string | DateWithEmbryoAge>();

  readonly columns: Column[] = ['month', 'day', 'year'];
  readonly embryoAges = [3, 5] as const;
  embryoAgeDays: 3 | 5 = 3;

  date = new Date();
  private min = new Date();
  private max = new Date();
  private drag: { column: Column; startY: number; moved: number } | null = null;
  private dragged = false;

  ngOnInit() {
    this.min = daysFromToday(this.question.minDaysFromToday);
    this.max = daysFromToday(this.question.maxDaysFromToday);
    this.date = this.clamp(daysFromToday(this.question.initialDaysFromToday ?? 0));
  }

  /** The column's text for the selected date moved by `offset` rows, or null when that's out of range. */
  label(column: Column, offset: number): string | null {
    const date = offset ? shift(this.date, column, offset) : this.date;
    if (date < this.min || date > this.max) return null;
    switch (column) {
      case 'month': return MONTHS[date.getMonth()];
      case 'day': return String(date.getDate());
      case 'year': return String(date.getFullYear());
    }
  }

  step(column: Column, by: number) {
    this.date = this.clamp(shift(this.date, column, by));
  }

  /** Clicking a neighbour row moves to it, unless the click ends a drag. */
  pick(column: Column, by: number) {
    if (this.dragged) {
      this.dragged = false;
      return;
    }
    this.step(column, by);
  }

  onWheel(column: Column, event: WheelEvent) {
    event.preventDefault();
    this.step(column, Math.sign(event.deltaY));
  }

  onPointerDown(column: Column, event: PointerEvent) {
    this.drag = { column, startY: event.clientY, moved: 0 };
    this.dragged = false;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event: PointerEvent) {
    if (!this.drag) return;
    // Dragging up brings the next (later) value into the box
    const steps = Math.round((this.drag.startY - event.clientY) / DRAG_STEP);
    if (steps !== this.drag.moved) {
      this.step(this.drag.column, steps - this.drag.moved);
      this.drag.moved = steps;
      this.dragged = true;
    }
  }

  onPointerUp() {
    this.drag = null;
  }

  submit() {
    const date = toIsoDate(this.date);
    this.answer.emit(this.question.embryoAge ? { date, embryoAgeDays: this.embryoAgeDays } : date);
  }

  private clamp(date: Date): Date {
    return date < this.min ? this.min : date > this.max ? this.max : date;
  }
}

/** Local midnight `days` from today. */
function daysFromToday(days: number): Date {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** Moves a date by whole days, months or years; months and years keep the day where the month allows. */
function shift(date: Date, column: Column, by: number): Date {
  if (column === 'day') {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + by);
  }
  const months = column === 'month' ? by : by * 12;
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const daysInMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), daysInMonth));
}

function toIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
